// useFrameworkConfig — the tenant's Disposition Framework config (target systems + captured EUC
// answers), persisted in framework_snapshots via /api/framework (durable + shared across the
// tenant's users). Replaces the earlier browser-only localStorage. Optimistic: local state updates
// immediately, the PUT fires in the background and failures are swallowed (the screen still works,
// falling back to ruleset defaults), so persistence never blocks or breaks the UI.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api';

export interface FrameworkConfig {
  targets: Record<string, string>;                 // boxId -> selected platform (tenant-wide default)
  customTargets: Record<string, string[]>;          // boxId -> custom platforms added
  // Item 7 (Asana 1216373781992284): eucId -> selected platform. An explicit PER-EUC override of
  // the box's tenant-wide default above — "Box 2 might legitimately offer Hex, Databricks
  // Notebooks, Snowpark, governed Alteryx and Power BI... the consultant picks which one THIS EUC
  // goes to." Prefer reading DispositionRecord.selected_target_platform (server-resolved, already
  // falls back through this -> targets[boxId] -> the ruleset default) over this map directly —
  // it's exposed mainly so the dropdown can show an optimistic value right after a save.
  eucTargets: Record<string, string>;
  answers: Record<string, Record<string, string>>;  // eucId -> { questionN: answer }
  loaded: boolean;
  setTarget: (boxId: string, value: string) => void;
  addCustomTarget: (boxId: string, raw: string) => void;
  setEucTarget: (eucId: string, value: string) => void;
  setAnswer: (eucId: string, n: number, text: string) => void;
}

export function useFrameworkConfig(): FrameworkConfig {
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [customTargets, setCustomTargets] = useState<Record<string, string[]>>({});
  const [eucTargets, setEucTargets] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get('/api/framework/config');
        const ts = res?.data?.targetSystems || {};
        if (!alive) return;
        setTargets(ts.selection && typeof ts.selection === 'object' ? ts.selection : {});
        setCustomTargets(ts.custom && typeof ts.custom === 'object' ? ts.custom : {});
        setEucTargets(ts.by_euc && typeof ts.by_euc === 'object' ? ts.by_euc : {});
        setAnswers(res?.data?.answers && typeof res.data.answers === 'object' ? res.data.answers : {});
      } catch {
        // Route not mounted / no data / auth — keep defaults (tiles fall back to catalog[0]).
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  // The whole { selection, custom, by_euc } object is re-sent on every save (PUT_TARGETS_SQL
  // replaces the column outright, same as the pre-existing selection/custom behavior) — so every
  // setter below reads all three pieces of current state before persisting, never just its own.
  const persistTargets = useCallback((selection: Record<string, string>, custom: Record<string, string[]>, byEuc: Record<string, string>) => {
    apiClient.put('/api/framework/target-systems', { selection, custom, by_euc: byEuc }).catch(() => { /* best-effort */ });
  }, []);
  const persistAnswers = useCallback((a: Record<string, Record<string, string>>) => {
    apiClient.put('/api/framework/euc-answers', { answers: a }).catch(() => { /* best-effort */ });
  }, []);

  const setTarget = useCallback((boxId: string, value: string) => {
    setTargets((prev) => {
      const next = { ...prev, [boxId]: value };
      setCustomTargets((cust) => {
        setEucTargets((byEuc) => { persistTargets(next, cust, byEuc); return byEuc; });
        return cust;
      });
      return next;
    });
  }, [persistTargets]);

  const addCustomTarget = useCallback((boxId: string, raw: string) => {
    const v = raw.trim();
    if (!v) return;
    setCustomTargets((prevC) => {
      const list = prevC[boxId] || [];
      const nextC = list.includes(v) ? prevC : { ...prevC, [boxId]: [...list, v] };
      setTargets((prevT) => {
        const nextT = { ...prevT, [boxId]: v };
        setEucTargets((byEuc) => { persistTargets(nextT, nextC, byEuc); return byEuc; });
        return nextT;
      });
      return nextC;
    });
  }, [persistTargets]);

  // Item 7: a per-EUC override. Validated server-side against that EUC's actual box catalog
  // (routes/framework.js) — a rejected PUT here just leaves the previous value in place client-
  // side too, since the optimistic update below only commits after the request is issued, not
  // after it's confirmed; the next /config or /dispositions read is the real source of truth.
  const setEucTarget = useCallback((eucId: string, value: string) => {
    setEucTargets((prev) => {
      const next = { ...prev, [eucId]: value };
      setTargets((t) => {
        setCustomTargets((cust) => { persistTargets(t, cust, next); return cust; });
        return t;
      });
      return next;
    });
  }, [persistTargets]);

  const setAnswer = useCallback((eucId: string, n: number, text: string) => {
    setAnswers((prev) => {
      const forEuc = { ...(prev[eucId] || {}) };
      const t = text.trim();
      if (t) forEuc[n] = t; else delete forEuc[n];
      const next = { ...prev, [eucId]: forEuc };
      persistAnswers(next);
      return next;
    });
  }, [persistAnswers]);

  return { targets, customTargets, eucTargets, answers, loaded, setTarget, addCustomTarget, setEucTarget, setAnswer };
}
